import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {ArchiveOptions, OTHER_PURPOSE} from 'archive/ArchiveConstants';
import * as ArchiveModal from 'archive/components/modals/ArchiveModal';
import {assert, assertAndContinue} from 'Asserts';
import * as AuditActions from 'audit/AuditActions';
import ExportAuditLogButton from 'audit/ExportAuditLogButton';
import {ActionMethod, ActionOrigin, RequestV2StatusType} from 'graphql-client/__generated/graphqlGlobalTypes';
import ResourcePresenceManager from 'nav/presence/ResourcePresenceManager';
import ResourcePresenceView from 'nav/presence/ResourcePresenceView';
import DiscardRequestV2SubmissionModal from 'processes/requests_v2/modals/DiscardRequestV2SubmissionModal';
import * as RequestV2SubmissionDAO from 'processes/requests_v2/network/RequestV2SubmissionDAO';
import RequestV2StatusDisplay from 'processes/requests_v2/RequestV2StatusDisplay';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import {Header1} from 'ui-library/Headers';
import EditableTextField from 'ui-library/inputs/EditableTextField';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import {OverflowButton} from 'ui-library/menus/OverflowMenu';
import runModal from 'ui-library/runModal';
import * as modals from 'util/Modals';
import {withLoadingScreen} from 'util/Promises';
import {assertNever} from 'util/Types';

import {
  getRequestV2SubmissionDetailsFromApiResponse,
  RequestV2SubmissionDetails,
  writeRequestV2SubmissionUpdateToCache,
} from './RequestV2SubmissionDetailsQueryUtils';
import {MAX_REQUEST_SUBMISSION_NAME_LENGTH} from './RequestV2SubmissionUtils';

interface RequestV2SubmissionHeaderProps {
  requestV2Submission: RequestV2SubmissionDetails;
  isSendDisabled: boolean;
  onSend: () => void;
  isRequestLocked: boolean;
}

interface RequestV2SubmissionActionButtonsProps {
  requestV2Submission: RequestV2SubmissionDetails;
  isSendDisabled: boolean;
  onSend: () => void;
  isRequestLocked: boolean;
}

export async function updateRequestV2Submission(updateKeysWithLoadingText: {
  id: string;
  status?: RequestV2StatusType;
  isArchived?: boolean;
  archiveReason?: string;
  name?: string;
  loadingScreenText: string;
}): Promise<void> {
  const {loadingScreenText, ...updateRequestArgs} = updateKeysWithLoadingText;
  const {requestV2Submissions} = await withLoadingScreen(
    RequestV2SubmissionDAO.updateRequestV2Submission({
      ...updateRequestArgs,
      actionOrigin: ActionOrigin.REQUEST_V2_SUBMISSION,
      actionMethod: ActionMethod.DIRECT_EDIT,
    }),
    loadingScreenText
  );
  assert(requestV2Submissions.length === 1, 'Expected exactly one request submission to be updated', {
    requestV2Submissions,
  });
  const updatedRequestV2Submission = getRequestV2SubmissionDetailsFromApiResponse(requestV2Submissions[0]);
  writeRequestV2SubmissionUpdateToCache(updatedRequestV2Submission);
}

async function cancelRequestV2Submission(requestV2SubmissionId: string): Promise<void> {
  const confirmedCancel = await Promise.resolve(
    modals.confirm(
      'Cancel request?',
      <div>
        Cancelling will stop work on this request. Tasks that are pending or in progress will be marked as
        "Cancelled" or "Invalid".
        <p />
        <p />
        This action cannot be undone.
      </div>,
      ButtonStyle.PRIMARY,
      {
        acceptText: 'Yes, cancel request',
        rejectText: "Don't cancel request",
        promiseCompatible: true,
        enableRedesign: true,
      }
    )
  );

  if (!confirmedCancel) {
    return;
  }

  await updateRequestV2Submission({
    id: requestV2SubmissionId,
    status: RequestV2StatusType.CANCELLED,
    loadingScreenText: 'Cancelling request...',
  });
}

function RequestV2SubmissionActionButtons(props: RequestV2SubmissionActionButtonsProps): JSX.Element {
  const isWritable = props.requestV2Submission.permissions.writable;
  const isArchivable = props.requestV2Submission.permissions.archivable ?? false;
  const isArchived = props.requestV2Submission.archiveData.archived;
  const isDiscardOrCancelAllowed = isWritable && isArchivable;
  const isSendAllowed = isWritable && !props.isSendDisabled;

  const isArchiveOrUnarchiveOptionEnabled = isArchived
    ? (props.requestV2Submission.permissions.unarchivable ?? false)
    : isArchivable;

  const sendDisabledMessage = props.isSendDisabled
    ? 'Submit tasks before sending your request'
    : 'You have insufficient permissions to send requests';

  const exportAuditLogButton = (
    <ExportAuditLogButton
      className={css(styles.button)}
      ids={[props.requestV2Submission.id]}
      buttonSize={ButtonSize.MEDIUM}
      buttonStyle={ButtonStyle.TERTIARY_GRAY}
    />
  );

  const handleRequestSubmissionArchiveAction = async (): Promise<void> => {
    if (isArchived) {
      await updateRequestV2Submission({
        id: props.requestV2Submission.id,
        isArchived: false,
        loadingScreenText: 'Unarchiving request...',
      });
    } else {
      ArchiveModal.open({
        items: [{id: props.requestV2Submission.id, name: props.requestV2Submission.name}],
        onArchive: async (itemIds: Array<string>, purpose: string, opts: ArchiveOptions): Promise<void> => {
          assertAndContinue(
            itemIds.length === 1 && itemIds[0] === props.requestV2Submission.id,
            'Expected exactly one request submission to be archived',
            {itemIds, requestV2SubmissionId: props.requestV2Submission.id}
          );
          await updateRequestV2Submission({
            id: props.requestV2Submission.id,
            isArchived: true,
            archiveReason: purpose,
            loadingScreenText: 'Archiving request...',
          });
        },
      });
    }
  };

  const overflowMenu = (
    <Dropdown
      menu={
        <Menu dropLeft={true} height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.LARGE}>
          <Option
            icon="audit-trail"
            onSelect={() => {
              AuditActions.exportAuditLogs([props.requestV2Submission.id]);
            }}
          >
            Export audit log
          </Option>
          {isArchiveOrUnarchiveOptionEnabled && (
            <Option
              icon={isArchived ? 'unarchive' : 'archive'}
              onSelect={handleRequestSubmissionArchiveAction}
            >
              {`${isArchived ? 'Unarchive' : 'Archive'} request`}
            </Option>
          )}
        </Menu>
      }
    >
      <OverflowButton
        className={css(styles.button)}
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        size={ButtonSize.MEDIUM}
        enableTooltip={false}
        isIconHorizontal={false}
      />
    </Dropdown>
  );

  switch (props.requestV2Submission.status) {
    case RequestV2StatusType.DRAFT:
      return (
        <>
          {exportAuditLogButton}
          <Tooltip
            placement={FloatingPlacement.TOP}
            text="You have insufficient permissions to discard this request"
            isDisabled={isDiscardOrCancelAllowed}
          >
            <Button
              className={css(styles.button)}
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              size={ButtonSize.MEDIUM}
              disabled={!isDiscardOrCancelAllowed}
              text="Discard"
              onClick={() => {
                runModal(DiscardRequestV2SubmissionModal, {
                  onRequestSubmit: async () => {
                    await updateRequestV2Submission({
                      id: props.requestV2Submission.id,
                      status: RequestV2StatusType.DISCARDED,
                      isArchived: true,
                      archiveReason: OTHER_PURPOSE,
                      loadingScreenText: 'Discarding request...',
                    });
                  },
                });
              }}
            />
          </Tooltip>
          <Tooltip placement={FloatingPlacement.TOP} text={sendDisabledMessage} isDisabled={isSendAllowed}>
            <Button
              className={css(styles.button)}
              buttonStyle={ButtonStyle.PRIMARY}
              size={ButtonSize.MEDIUM}
              disabled={!isSendAllowed}
              text="Send request"
              onClick={props.onSend}
              dataTestAttrs={{component: 'RequestV2SubmissionHeader', element: 'sendRequestButton'}}
            />
          </Tooltip>
        </>
      );
    case RequestV2StatusType.DISCARDED:
      return (
        <>
          {exportAuditLogButton}
          {isArchiveOrUnarchiveOptionEnabled && (
            <Button
              className={css(styles.button)}
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              size={ButtonSize.MEDIUM}
              icon={isArchived ? 'unarchive' : 'archive'}
              onClick={handleRequestSubmissionArchiveAction}
              dataTestAttrs={{
                component: 'RequestV2SubmissionHeader',
                element: isArchived ? 'unarchiveButton' : 'archiveButton',
              }}
            />
          )}
        </>
      );
    case RequestV2StatusType.SENT:
      return (
        <>
          <Tooltip
            placement={FloatingPlacement.TOP}
            text="You have insufficient permissions to cancel this request"
            isDisabled={isDiscardOrCancelAllowed}
          >
            <Button
              className={css(styles.button)}
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              size={ButtonSize.MEDIUM}
              disabled={!isDiscardOrCancelAllowed}
              text="Cancel request"
              onClick={async () => {
                await cancelRequestV2Submission(props.requestV2Submission.id);
              }}
              dataTestAttrs={{component: 'RequestV2SubmissionHeader', element: 'cancelRequestButton'}}
            />
          </Tooltip>
          {overflowMenu}
        </>
      );
    case RequestV2StatusType.FULFILLED:
    case RequestV2StatusType.CANCELLED:
      return overflowMenu;
    default:
      assert(
        false,
        `Buttons for request status ${assertNever(props.requestV2Submission.status)} not supported`
      );
  }
}

export default function RequestV2SubmissionHeader(props: RequestV2SubmissionHeaderProps): JSX.Element {
  return (
    <div className={css(styles.headerContainer)}>
      <ResourcePresenceManager fileId={props.requestV2Submission.document.id}>
        <div className={css(styles.nameAndStatusContainer)}>
          <Header1>
            <EditableTextField
              value={props.requestV2Submission.name}
              requestChange={(newName: string) => {
                updateRequestV2Submission({
                  id: props.requestV2Submission.id,
                  name: newName,
                  loadingScreenText: 'Updating name...',
                });
              }}
              isDisabled={props.isRequestLocked}
              maxLength={MAX_REQUEST_SUBMISSION_NAME_LENGTH}
              hasFieldActionButtonsOuterMargin={false}
              inputClassName={css(styles.nameInput)}
              dataTestAttrs={{component: 'RequestV2SubmissionHeader', element: 'title'}}
            />
          </Header1>
          <div className={css(styles.statusContainer)}>
            <RequestV2StatusDisplay status={props.requestV2Submission.status} />
          </div>
        </div>
        <div className={css(styles.buttonContainer)}>
          <div className={css(styles.resourcePresenceContainer)}>
            <ResourcePresenceView resourceId={props.requestV2Submission.document.id} />
          </div>
          <RequestV2SubmissionActionButtons
            requestV2Submission={props.requestV2Submission}
            isSendDisabled={props.isSendDisabled}
            onSend={props.onSend}
            isRequestLocked={props.isRequestLocked}
          />
        </div>
      </ResourcePresenceManager>
    </div>
  );
}

const styles = StyleSheet.create({
  nameAndStatusContainer: {
    display: 'flex',
    flexDirection: 'row',
  },
  statusContainer: {
    display: 'flex',
    padding: 4,
  },
  headerContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  button: {
    marginRight: '8px',
  },
  resourcePresenceContainer: {
    alignItems: 'center',
    display: 'flex',
    marginRight: '8px',
  },
  nameInput: {
    fontSize: '17px',
  },
});
